import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Mic,
  MicOff,
  MonitorPlay,
  SlidersHorizontal,
  Square,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService, { type DemoStudioSession } from '@/lib/api/demoStudio';
import { useAudioRecording } from '@/hooks/useAudioRecording';

const ACTIVE_STATUSES = new Set(['launching', 'active', 'processing']);

const DemoStudioSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [stopping, setStopping] = React.useState(false);
  const [uploadingAudio, setUploadingAudio] = React.useState(false);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [session, setSession] = React.useState<DemoStudioSession | null>(null);
  const { micStatus, startRecording, stopRecording } = useAudioRecording();
  const timingMarksRef = React.useRef({ visible: false, narration_start: false });

  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: root?.style.overflow ?? '',
    };

    html.style.overflow = 'auto';
    body.style.overflow = 'auto';
    if (root) {
      root.style.overflow = 'auto';
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      if (root) {
        root.style.overflow = previous.rootOverflow;
      }
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await demoStudioService.getSession(sessionId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load Demo Studio session.');
        }

        if (!cancelled) {
          setSession(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Unable to load session',
            description: error instanceof Error ? error.message : 'Failed to load Demo Studio session.',
            variant: 'destructive',
          });
          navigate('/demo-studio', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSession();

    return () => {
      cancelled = true;
    };
  }, [navigate, sessionId, toast]);

  React.useEffect(() => {
    timingMarksRef.current = {
      visible: Boolean(session?.visible_start_ms != null),
      narration_start: Boolean(session?.narration_start_ms != null),
    };
  }, [session?.id, session?.visible_start_ms, session?.narration_start_ms]);

  const markTimingEvent = React.useCallback(
    async (eventType: 'visible' | 'narration_start') => {
      if (!session?.id || timingMarksRef.current[eventType]) {
        return;
      }

      const response = await demoStudioService.markTimingEvent(session.id, eventType);
      if (!response.success || !response.data) {
        throw new Error(response.error || `Failed to mark ${eventType} timing.`);
      }

      timingMarksRef.current[eventType] = true;
      setSession(response.data);
    },
    [session?.id],
  );

  const uploadNarrationBlob = React.useCallback(
    async (blob: Blob | null, showSuccessToast: boolean = true) => {
      if (!blob || blob.size === 0 || !session?.id) {
        return null;
      }

      setUploadingAudio(true);
      try {
        const file = new File([blob], 'narration.webm', {
          type: blob.type || 'audio/webm',
        });
        const response = await demoStudioService.uploadAudio(session.id, file);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to upload narration.');
        }

        setSession(response.data.session);
        if (showSuccessToast) {
          toast({
            title: 'Narration saved',
            description: 'Your voice track has been uploaded to this demo session.',
          });
        }
        return response.data.session;
      } catch (error) {
        toast({
          title: 'Narration upload failed',
          description: error instanceof Error ? error.message : 'Failed to upload narration.',
          variant: 'destructive',
        });
        return null;
      } finally {
        setUploadingAudio(false);
      }
    },
    [session?.id, toast],
  );

  const handleStartNarration = React.useCallback(async () => {
    const status = await startRecording();
    if (status === 'denied') {
      toast({
        title: 'Microphone access denied',
        description: 'Allow microphone access in your browser to record narration.',
        variant: 'destructive',
      });
      return;
    }
    if (status === 'error') {
      toast({
        title: 'Microphone unavailable',
        description: 'Demo Studio could not start microphone recording.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await markTimingEvent('narration_start');
    } catch (error) {
      toast({
        title: 'Timing sync unavailable',
        description: error instanceof Error ? error.message : 'Failed to mark narration start timing.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Narration recording',
      description: 'Your microphone audio is now being captured for this session.',
    });
  }, [markTimingEvent, startRecording, toast]);

  const handleStopNarration = React.useCallback(
    async (showSuccessToast: boolean = true) => {
      const blob = await stopRecording();
      return uploadNarrationBlob(blob, showSuccessToast);
    },
    [stopRecording, uploadNarrationBlob],
  );

  const handleStop = React.useCallback(async () => {
    if (!session?.id) return;

    setStopping(true);
    try {
      if (micStatus === 'active') {
        await handleStopNarration(false);
      }

      const response = await demoStudioService.stopSession(session.id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to stop Demo Studio session.');
      }

      setSession(response.data);
      toast({
        title: 'Session stopped',
        description: 'The managed browser session has been closed.',
      });
    } catch (error) {
      toast({
        title: 'Stop failed',
        description: error instanceof Error ? error.message : 'Failed to stop Demo Studio session.',
        variant: 'destructive',
      });
    } finally {
      setStopping(false);
    }
  }, [handleStopNarration, micStatus, session?.id, toast]);

  const isActive = Boolean(session && ACTIVE_STATUSES.has(session.status));
  const liveViewUrl = session?.live_view_url ?? null;
  const narrationAvailable = Boolean(session?.raw_audio_path);
  const controlsDisabled = loading || stopping || uploadingAudio || !isActive;

  const studioPanel = (
    <div className="flex h-full flex-col gap-3">
      <Card className="border card-border shadow-none">
        <CardHeader>
          <CardTitle className="text-base">Studio panel</CardTitle>
          <CardDescription>
            This rail stays inside the Hobbes session shell for guidance and controls.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
            Live browser control surface stays front and center.
          </div>
          <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
            Chat guidance, login help, and richer recording controls can layer in here next.
          </div>
          <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
            Narration:
            {' '}
            {uploadingAudio
              ? 'uploading voice track'
              : micStatus === 'active'
                ? 'recording microphone audio'
                : narrationAvailable
                  ? 'voice track saved'
                  : 'not recording'}
          </div>
          {session?.browserbase_session_id && (
            <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
              Live session: {session.browserbase_session_id}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Demo Studio Session | Hobbes</title>
      </Helmet>

      <div className="min-h-screen overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_42%),linear-gradient(180deg,_hsl(var(--background)),_hsl(var(--background)))] text-foreground">
        <div className="flex min-h-screen flex-col">
          <header className="border-b border-border/70 bg-background/90 backdrop-blur">
            <div className="flex flex-col gap-3 px-3 py-3 sm:px-4 lg:px-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" asChild className="h-8 gap-1.5 px-2">
                    <Link to="/demo-studio">
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Link>
                  </Button>
                  <Badge variant="secondary" className="gap-1.5">
                    <MonitorPlay className="h-3 w-3" />
                    Demo Studio
                  </Badge>
                  {session && <Badge variant="outline">Status: {session.status}</Badge>}
                  {session?.browserbase_region && (
                    <Badge variant="outline">{session.browserbase_region}</Badge>
                  )}
                  <Badge variant="outline">Recording output unchanged</Badge>
                  {narrationAvailable && <Badge variant="outline">Narration uploaded</Badge>}
                </div>
                <div className="mt-2">
                  <h1 className="text-lg font-semibold tracking-tight sm:text-xl">
                    {session?.title || 'Managed recording workspace'}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Stay in this workspace while you log in, navigate the product, and record the demo.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPanelOpen(true)}
                  className="gap-2"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Studio panel
                </Button>
                <Button
                  variant={micStatus === 'active' ? 'secondary' : 'outline'}
                  onClick={micStatus === 'active' ? () => void handleStopNarration() : () => void handleStartNarration()}
                  disabled={controlsDisabled}
                  className="gap-2"
                >
                  {uploadingAudio ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading narration...
                    </>
                  ) : micStatus === 'active' ? (
                    <>
                      <MicOff className="h-4 w-4" />
                      Stop narration
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4" />
                      Start narration
                    </>
                  )}
                </Button>
                {liveViewUrl && (
                  <Button variant="outline" asChild className="gap-2">
                    <a href={liveViewUrl} target="_blank" rel="noreferrer">
                      Open in new tab
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={handleStop}
                  disabled={!session?.id || !isActive || stopping || uploadingAudio}
                  className="gap-2"
                >
                  {stopping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Stopping...
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      End session
                    </>
                  )}
                </Button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-2 pb-3 pt-2 sm:px-3 sm:pb-4">
            <div className="flex flex-col rounded-[28px] border border-border/70 bg-[linear-gradient(180deg,rgba(15,23,42,0.08),rgba(15,23,42,0.02))] p-2 shadow-[0_28px_100px_rgba(15,23,42,0.18)] sm:p-3">
              <div className="mb-3 flex items-center justify-between gap-3 rounded-[22px] border border-border/70 bg-background/80 px-4 py-3 backdrop-blur">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      Live browser canvas
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                    The recording resolution stays fixed. This shell only improves the in-platform feel while you record.
                  </p>
                </div>
                <div className="hidden items-center gap-2 lg:flex">
                  <div className="rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
                    Optimized for a browser-shaped canvas
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="w-full max-w-[1720px]">
                  <section className="overflow-hidden rounded-[28px] border border-zinc-800/80 bg-black shadow-[0_36px_120px_rgba(2,6,23,0.55)]">
                    <AspectRatio ratio={16 / 9} className="bg-black">
                      {loading ? (
                        <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.22),_transparent_38%),linear-gradient(180deg,#090d18,#05070d)]">
                          <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading workspace...
                          </div>
                        </div>
                      ) : liveViewUrl && isActive ? (
                        <iframe
                          src={liveViewUrl}
                          title="Demo Studio Live View"
                          className="block h-full w-full bg-black"
                          allow="clipboard-read; clipboard-write"
                          onLoad={() => {
                            void markTimingEvent('visible').catch(() => undefined);
                          }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,#090d18,#05070d)] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="text-white">Session unavailable</CardTitle>
                              <CardDescription className="text-white/70">
                                The live workspace is no longer active. Return to Demo Studio to launch a new session.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Button asChild>
                                <Link to="/demo-studio">Back to Demo Studio</Link>
                              </Button>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </AspectRatio>
                  </section>
                </div>
              </div>
            </div>
          </main>
        </div>

        <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
          <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-md">
            <SheetHeader className="border-b border-border/70 px-6 py-5">
              <SheetTitle>Studio panel</SheetTitle>
              <SheetDescription>
                Session controls and future in-product guidance live here without shrinking the browser.
              </SheetDescription>
            </SheetHeader>
            <div className="p-4">
              {studioPanel}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};

export default DemoStudioSessionPage;
